'use strict';

import { evaluatePath, setPath } from 'doc-path';
import { defaultJson2CsvOptions, defaultCsv2JsonOptions } from './constants';
import type {
    Json2CsvOptions,
    Csv2JsonOptions,
    FullJson2CsvOptions,
    FullCsv2JsonOptions,
    HeaderField,
} from './types';

const dateStringRegex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/,
    escapedJsonRegex = new RegExp(/(?<=\{+)""|""(?=\}+,)|(?<=\[+)""|""(?=\]+)|(?<=\:)""|""(?=\:)|(?<=\,)""|""(?=\,)|""(?=\}+$)|""(?=\}+]+$|^""|""$)/, 'g'),
    MAX_ARRAY_LENGTH = 100000;

export function getEscapedWrapDelimiterRegex(options: FullJson2CsvOptions | FullCsv2JsonOptions) {
    return new RegExp(options.delimiter.wrap + options.delimiter.wrap, 'g');
}

// Following 2 constants are also linked in @cic/utils package.
// If making any change in these 2, please also make sure to update 
// the file `libs/utils/src/CommonUtils.ts` in @cic/utils package.
export const DELETE_PROPERTY_NAME = '_DELETE_';
export const PARENT_PROPERTY_NAME = '_PARENT_';
export const VALUE_PROPERTY_NAME = '_VALUE_';
export const ARRAY_INDEX_HEADER = '_INDEX_';

/**
 * Build the options to be passed to the appropriate function
 * If a user does not provide custom options, then we use our default
 * If options are provided, then we set each valid key that was passed
 */
export function buildJ2COptions(opts: Json2CsvOptions): FullJson2CsvOptions {
    return {
        ...defaultJson2CsvOptions,
        ...opts,
        delimiter: {
            field:
                opts?.delimiter?.field ??
                defaultJson2CsvOptions.delimiter.field,
            wrap:
                opts?.delimiter?.wrap || defaultJson2CsvOptions.delimiter.wrap,
            eol: opts?.delimiter?.eol || defaultJson2CsvOptions.delimiter.eol,
        },
        fieldTitleMap: Object.create({}),
    };
}

/**
 * Build the options to be passed to the appropriate function
 * If a user does not provide custom options, then we use our default
 * If options are provided, then we set each valid key that was passed
 */
export function buildC2JOptions(opts: Csv2JsonOptions): FullCsv2JsonOptions {
    return {
        ...defaultCsv2JsonOptions,
        ...opts,
        delimiter: {
            field:
                opts?.delimiter?.field ??
                defaultCsv2JsonOptions.delimiter.field,
            wrap:
                opts?.delimiter?.wrap || defaultCsv2JsonOptions.delimiter.wrap,
            eol: opts?.delimiter?.eol || defaultCsv2JsonOptions.delimiter.eol,
        },
    };
}

export function validate(
    data: unknown,
    validationFn: (data: unknown) => boolean,
    errorMessages: Record<string, string>
) {
    if (!data) throw new Error(`${errorMessages.cannotCallOn} ${data}.`);
    if (!validationFn(data)) throw new Error(errorMessages.dataCheckFailure);
    return true;
}

/**
 * Utility function to deep copy an object, used by the module tests
 */
export function deepCopy<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
}

/**
 * Helper function that determines whether the provided value is a representation
 *   of a string. Given the RFC4180 requirements, that means that the value is
 *   wrapped in value wrap delimiters (usually a quotation mark on each side).
 */
export function isStringRepresentation(
    fieldValue: string,
    options: FullJson2CsvOptions | FullCsv2JsonOptions
) {
    const firstChar = fieldValue[0],
        lastIndex = fieldValue.length - 1,
        lastChar = fieldValue[lastIndex];

    // If the field starts and ends with a wrap delimiter
    return (
        firstChar === options.delimiter.wrap &&
        lastChar === options.delimiter.wrap
    );
}

/**
 * Helper function that determines whether the provided value is a representation
 *   of a date.
 */
export function isDateRepresentation(fieldValue: string) {
    return dateStringRegex.test(fieldValue);
}

/**
 * Helper function that determines the schema differences between two objects.
 */
export function computeSchemaDifferences<T>(schemaA: T[], schemaB: T[]): T[] {
    return arrayDifference(schemaA, schemaB).concat(
        arrayDifference(schemaB, schemaA)
    );
}

/**
 * Utility function to check if a field is considered empty so that the emptyFieldValue can be used instead
 */
export function isEmptyField(fieldValue: unknown) {
    return isUndefined(fieldValue) || isNull(fieldValue) || fieldValue === '' || fieldValue === `[""]`;
}

/**
 * Helper function that removes empty field values from an array.
 */
export function removeEmptyFields(fields: unknown[]) {
    return fields.filter((field: unknown) => !isEmptyField(field));
}

/**
 * Helper function that retrieves the next n characters from the start index in
 *   the string including the character at the start index. This is used to
 *   check if are currently at an EOL value, since it could be multiple
 *   characters in length (eg. '\r\n')
 */
export function getNCharacters(str: string, start: number, n: number) {
    return str.substring(start, start + n);
}

/**
 * The following unwind functionality is a heavily modified version of @edwincen's
 * unwind extension for lodash. Since lodash is a large package to require in,
 * and all of the required functionality was already being imported, either
 * natively or with doc-path, I decided to rewrite the majority of the logic
 * so that an additional dependency would not be required. The original code
 * with the lodash dependency can be found here:
 *
 * https://github.com/edwincen/unwind/blob/master/index.js
 */

/**
 * Core function that unwinds an item at the provided path
 */
function unwindItem(accumulator: unknown[], item: unknown, fieldPath: string) {
    const evaluatedPath = evaluatePath(item, fieldPath);
    let cloned = deepCopy(item);
    if (!evaluatedPath) {
        accumulator.push(cloned);
        return;
    }

    const valueToUnwind = deepCopy(evaluatedPath);

    if (Array.isArray(valueToUnwind) && valueToUnwind.length) {
        valueToUnwind.forEach((val, index) => {
            if (!val) {
                cloned = deepCopy(item);
                accumulator.push(setPath(cloned, fieldPath, val));
                return;
            }

            if (isObject(val)) {
                // @ts-ignore
                val[ARRAY_INDEX_HEADER] = index;
            }
            else {
                accumulator.push(setPath(cloned, fieldPath, {
                    [VALUE_PROPERTY_NAME]: val,
                    [ARRAY_INDEX_HEADER]: index
                }));
            }

            cloned = deepCopy(item);
            accumulator.push(setPath(cloned, fieldPath, val));
        });
    } else if (Array.isArray(valueToUnwind) && valueToUnwind.length === 0) {
        // Push an empty string so the value is empty since there are no values
        setPath(cloned, fieldPath, '');
        accumulator.push(cloned);
    } else {
        accumulator.push(cloned);
    }
}

/**
 * Unwind the array of numbers into columns instead of rows.
 */
function unwindNumbersInColumns<T>(array: T[], field: string) {
    const result: any[] = [];

    array.forEach((item) => {
        const valueToUnwind = evaluatePath(item, field);
        const cloned: any = deepCopy(item);

        if (Array.isArray(valueToUnwind) && valueToUnwind.length) {
            valueToUnwind.forEach((val, index) => {
                setPath(cloned, `${field}[${index}]`, val);
            });
            delete cloned[field];
            result.push(cloned);
        } else {
            unwindItem(result, item, field);
        }
    });

    return result;
}
/**
 * Main unwind function which takes an array and a field to unwind.
 */
export function unwind<T>(
    array: T[],
    field: string,
    options: FullJson2CsvOptions | FullCsv2JsonOptions
) {
    const result: T[] = [];
    const type = getPropertyType(
        field,
        options.schema?.anyOf ? options.schema.anyOf[0] : options.schema
    );
    const arrayType = getArrayType(
        field,
        options.schema?.anyOf ? options.schema.anyOf[0] : options.schema
    );

    if (typeIsArray(type) && typeIsNumber(arrayType)) {
        return unwindNumbersInColumns(array, field);
    } else {
        array.forEach((item) => {
            unwindItem(result, item, field);
        });
    }

    return result;
}

export function typeIsNumber(type: string | undefined) {
    return type === 'integer' || type === 'number';
}

/**
 * Checks whether value can be converted to a number
 */
export function isNumber(value: unknown) {
    return !isNaN(Number(value));
}

/*
 * Helper functions which were created to remove underscorejs from this package.
 */

export function typeIsString(type: string | undefined) {
    return type === 'string';
}

export function isString(value: unknown) {
    return typeof value === 'string';
}

export function typeIsObject(type: string | undefined) {
    return type === 'object';
}

export function isObject(value: unknown) {
    return typeof value === 'object';
}

export function isNull(value: unknown) {
    return value === null;
}

export function isUndefined(value: unknown) {
    return typeof value === 'undefined';
}

export function typeIsArray(type: string | undefined) {
    return type === 'array';
}

export function isBoolean(type: string | undefined) {
    return type === 'boolean';
}

export function toBoolean(value: string) {
    return value && (value === '1' || value.toLowerCase() === 'true');
}

export function isError(value: unknown) {
    // TODO(mrodrig): test this possible change
    // return value instanceof Error;
    return Object.prototype.toString.call(value) === '[object Error]';
}

export function arrayDifference<T>(a: T[], b: T[]) {
    return a.filter((x) => !b.includes(x));
}

export function unique<T>(array: T[]): T[] {
    return [...new Set(array)];
}

export function flatten<T>(array: T[][]): T[] {
    // Node 11+ - use the native array flattening function
    if (array.flat) {
        return array.flat();
    }

    // #167 - allow browsers to flatten very long 200k+ element arrays
    if (array.length > MAX_ARRAY_LENGTH) {
        let safeArray: T[] = [];
        for (let a = 0; a < array.length; a += MAX_ARRAY_LENGTH) {
            safeArray = safeArray.concat(
                ...array.slice(a, a + MAX_ARRAY_LENGTH)
            );
        }
        return safeArray;
    }
    return array.reduce((accumulator, value) => accumulator.concat(value), []);
}

/**
 * Used to help avoid incorrect values returned by JSON.parse when converting
 * CSV back to JSON, such as '39e1804' which JSON.parse converts to Infinity
 */
export function isInvalid(parsedJson: unknown) {
    return parsedJson === Infinity || parsedJson === -Infinity;
}

//
export function cleanArray(array: any[], regex: RegExp, wrapDelimiter: string) {
    return array.map((value) => {
        return typeof value === 'string'
            ? value.replace(regex, wrapDelimiter)
            : value;
    });
}

//
export function cleanBackslashes(array: string[]) {
    return array.map((str) => str?.replace(/\\/g, ''));
}

//
export function removeNumber(value: string) {
    return value.replace(/\[\d+\]/, '');
}

//
export function identifyUnwindColumnHeaders(
    headerFields: { value: string }[]
): string[] {
    const regex = /\[(\d+)]$/;
    return headerFields
        .filter((headerField) => regex.test(headerField.value))
        .map((headerField) => headerField.value);
}

//
export function identifyUnwindRowHeaders(
    headerFields: { value: string }[]
): string[] {
    return headerFields
        .filter((headerField) => headerField.value.includes('[]') || isDeleteControlHeader(headerField.value))
        .map((headerField) => headerField.value);
}

//
export function removeArrayNotation(headerValue: string): string {
    const regex = /\[\d*\]/g;
    return headerValue.replace(regex, '');
}

//
export function removeLangTagArrayNotation(headerValue: string): string {
    const regex = /\[[^\]]+\]/g;
    return headerValue.replace(regex, '');
}

//
export function removeArrayNotationFromHeaders(
    headerFields: HeaderField[]
): HeaderField[] {
    return headerFields.map((headerField) => {
        let updatedValue = removeArrayNotation(headerField.value);
        // Check if there are still [languageTag] left
        const pattern = /\[[^\]]+\]/;
        if (pattern.test(updatedValue)) {
            updatedValue = removeLangTagArrayNotation(updatedValue);
        }
        return { ...headerField, value: updatedValue };
    });
}

/**
 * Removes the outermost wrap delimiters from a value, if they are present
 * Otherwise, the non-wrapped value is returned as is
 */
export function removeWrapDelimitersFromValue(fieldValue: string, options: FullJson2CsvOptions | FullCsv2JsonOptions) {
    const firstChar = fieldValue[0],
        lastIndex = fieldValue.length - 1,
        lastChar = fieldValue[lastIndex];
    // If the field starts and ends with a wrap delimiter
    if (
        firstChar === options.delimiter.wrap &&
        lastChar === options.delimiter.wrap
    ) {
        return fieldValue.substr(1, lastIndex - 1);
    }
    return fieldValue;
}

/**
 * Unescapes wrap delimiters by replacing duplicates with a single (eg. "" -> ")
 * This is done in order to parse RFC 4180 compliant CSV back to JSON
 */
export function unescapeWrapDelimiterInField(fieldValue: string, options: FullJson2CsvOptions | FullCsv2JsonOptions) {
    if (fieldValue.startsWith('[') && fieldValue.endsWith(']')) {
        try {
            const parsedArray = JSON.parse(fieldValue);

            if (Array.isArray(parsedArray)) {
                const cleanedArray = cleanArray(
                    parsedArray,
                    getEscapedWrapDelimiterRegex(options),
                    options.delimiter.wrap
                );
                return JSON.stringify(cleanedArray);
            }
        } catch (error) {
            // Error parsing JSON. This occurs when the fieldValue is an array generated
            // by json2csv and was not created by the unwind function
            // Fall through to the regular replacement.
        }
    }

    if (fieldValue.startsWith('{') || fieldValue.startsWith('[{')) {
        return fieldValue.replace(
            escapedJsonRegex,
            options.delimiter.wrap
        );
    }
    else {
        return fieldValue.replace(
            getEscapedWrapDelimiterRegex(options),
            options.delimiter.wrap
        );
    }

}

/**
 * Trims the record value, if specified by the user via the options object
 */
export function trimRecordValue(fieldValue: string, options: FullJson2CsvOptions | FullCsv2JsonOptions) {
    if (options.trimFieldValues && fieldValue !== null) {
        return fieldValue.trim();
    }
    return fieldValue;
}

//
export function addArrayNotationToHeaders(
    keys: string[],
    options: FullJson2CsvOptions
) {
    const regex = /\[\d+\]$/;
    let augmented = [...keys];

    if (options.unwindKeys.length) {
        augmented = augmented.map((k) => {
            let replaced = k;

            options.unwindKeys
                .sort((a: string, b: string) => b.length - a.length)
                .forEach((unwindKey) => {
                    if (k.startsWith(unwindKey) && !k.match(regex)) {
                        if (
                            options.localizedKeys.some((k) => unwindKey === k)
                        ) {
                            // If the key is a language tag, we need to add the language tag to the array notation
                            const lastDotIndex: number =
                                replaced.lastIndexOf('.');
                            replaced = `${replaced}[${replaced.substring(
                                lastDotIndex + 1
                            )}]`;
                        } else {
                            replaced = replaced.replace(
                                unwindKey,
                                unwindKey + '[]'
                            );
                        }
                    }
                });
            return replaced;
        });
    }

    return augmented;
}

// Get the property type from the schema
export function getPropertyType(
    headerField: string,
    schema: any
): string | undefined {
    return getSchemaProperty(
        headerField,
        schema?.anyOf ? schema.anyOf[0] : schema
    )?.type;
}

// Get the array type from the schema
export function getArrayType(
    headerField: string,
    schema: any
): string | undefined {
    return getSchemaProperty(
        headerField,
        schema?.anyOf ? schema.anyOf[0] : schema
    )?.items?.type;
}

// Get the property from the schema based on the header field
function getSchemaProperty(headerField: string, schema: any): any {
    const fieldParts = removeIndexFromHeaderName(headerField).split('.');

    for (const part of fieldParts) {
        if (schema && schema.properties && schema.properties[part]) {
            schema = schema.properties[part];
        } else if (schema && schema.items && schema.items.properties[part]) {
            schema = schema.items.properties[part];
        } else if (schema && schema.additionalProperties) {
            schema = schema.additionalProperties;
        } else {
            return undefined;
        }
    }

    return schema;
}

/**
 * Excel saves those values in uppercase.
 */
export function convertToJSONKeyword(value: string) {
    switch (value) {
        case 'TRUE':
            return 'true';
        case 'FALSE':
            return 'false';
        case 'NULL':
            return 'null';
        default:
            return value;
    }
}

export function removeQuotes(value: string) {
    // Remove extra starting and ending quotes if any
    if ((value.startsWith('"') && value.endsWith('"')) ||
        (value.startsWith("'") && value.endsWith("'"))) {
        return value.slice(1, -1);
    } else {
        return value; // return the original value if no quotes found
    }
}

export function getHeaderPaths(header: string) {
    const splittedHeader = header.split('.');
    const headerPaths: string[] = [];
    let previousPath: string | undefined;

    for (const headerPart of splittedHeader) {
        const path = previousPath ? `${previousPath}.${headerPart}` : headerPart;
        headerPaths.push(path);
        previousPath = path;
    }

    return headerPaths;
}

export function removeIndexFromHeaderName(header: string) {
    return header.replace(`.${ARRAY_INDEX_HEADER}`, '');
}

export function isArrayIndexControlHeader(header: string) {
    return header.endsWith(ARRAY_INDEX_HEADER) || header.endsWith(VALUE_PROPERTY_NAME);
}

export function getArrayIndexControlColumn(header: string, headersMap: Map<string, string[]>) {
    const headerWithoutIndex = removeIndexFromHeaderName(header);
    const headerPaths = getHeaderPaths(headerWithoutIndex);

    for (let i = headerPaths.length - 1; i >= 0; i--) {
        const indexColumn = `${headerPaths[i]}.${ARRAY_INDEX_HEADER}`;

        if (headersMap.has(indexColumn)) {
            return indexColumn;
        }
    }

    return undefined;
}

export function isDeleteControlHeader(header: string) {
    return header === DELETE_PROPERTY_NAME;
}

export function doubleQuotesToEscaped(value: string) {
    if (value.startsWith('[') && value.endsWith(']')) {
        try {
            const parsedArray = JSON.parse(value);

            if (Array.isArray(parsedArray)) {
                const cleanedArray = parsedArray.map((value) => {
                    return (typeof value === 'string') ? value.replace('""', '\\"') : value;
                });

                return JSON.stringify(cleanedArray);
            }
        } catch (error) { /* empty */ }
    }

    return value.replaceAll('""', '\\"');
}

export function escapedToDoubleQuotes(value: string) {
    if (value.startsWith('"{') || value.startsWith('"[{')) {
        return value.replaceAll('\\"', '""');
    }
    else {
        return value;
    }
}

export function buildMockRecord(records: any[], unwindKeys: string[] = []): any {
    if (!records.length) return {};

    // Build a single mock record that contains every key seen across all records.
    // This preserves the perf optimization in unwindRecordsIfNecessary where
    // retrieveHeaderFields() (deeks) only runs on 1 record instead of thousands.
    // We use flattenObjectKeys() (cheap, hand-rolled) on all records to union
    // their key sets, so heterogeneous sibling keys (e.g. dimensions.others.depth2
    // vs dimensions.others.width2) are all represented in the mock — without
    // paying the cost of running deeks on every record.
    const mockRecord: any = {};
    const allKeys = new Set<string>();
    for (const record of records) {
        flattenObjectKeys(record).forEach(k => allKeys.add(k));
    }
    for (const keyPath of allKeys) {
        assignNestedKey(mockRecord, keyPath, {});
    }

    const updatedUnwindKeys = unwindKeys?.includes('baseColorFactor')
        ? unwindKeys.flatMap(key =>
            key === 'baseColorFactor'
                ? ['baseColorFactor[0]', 'baseColorFactor[1]', 'baseColorFactor[2]', 'baseColorFactor[3]']
                : [key]
        ) : unwindKeys ?? [];

    const sortedUnwindKeys = [...updatedUnwindKeys].sort((a: string, b: string) => b.length - a.length);

    for (const key of sortedUnwindKeys) {
        const allKeys = new Set<string>();

        for (const record of records) {
            let value: any;
            if (key.includes(".")) {
                value = evaluatePath(record, key);
            } else {
                value = record[key];
            }

            if (Array.isArray(value)) {
                for (const item of value) {
                    if (item && typeof item === "object") {
                        flattenObjectKeys(item).forEach(k => allKeys.add(k));
                    }
                }
            } else if (value && typeof value === "object") {
                flattenObjectKeys(value).forEach(k => allKeys.add(k));
            }
        }

        const mockItem: any = {};
        for (const keyPath of allKeys) {
            assignNestedKey(mockItem, keyPath, null);
        }

        let originalValue: any = undefined;

        for (const record of records) {
            const val = key.includes(".") ? evaluatePath(record, key) : record[key];
            if (val !== null && val !== undefined) {
                originalValue = val;
                break;
            }
        }

        let valueToSet: any;
        if (Array.isArray(originalValue)) {
            valueToSet = [mockItem];
        } else if (originalValue && typeof originalValue === 'object' && VALUE_PROPERTY_NAME in originalValue) {
            valueToSet = [{}];
        } else {
            valueToSet = mockItem;
        }

        if (key.includes(".")) {
            setPath(mockRecord, key, valueToSet);
        } else {
            mockRecord[key] = valueToSet;
        }
    }

    return mockRecord;
}

export function cleanCsvEmbeddedJson(cell: string) {
    if (typeof cell !== 'string') return cell;
    let v = cell.trim();

    // Remove the CSV wrapper quotes if present
    if (v.startsWith('"') && v.endsWith('"')) v = v.slice(1, -1);

    try {
        JSON.parse(v);
        return v;
    } catch {
        // Not valid JSON, continue
    }

    // Undo CSV double-quote escaping: "" -> "
    v = v.replace(/""/g, '"');

    try {
        JSON.parse(v);
        return v;
    } catch {
        // Not valid JSON, continue
    }

    // Escape stray quotes inside values
    v = v.replace(/("[\w-]+"\s*:\s*")([\s\S]*?)("(?:\s*[,}]))/g, (_, start, middle, end) => {
        const fixed = middle.replace(/(?<!\\)"/g, '\\"');
        return start + fixed + end;
    });

    // Fix stray \" before a closing bracket/brace/comma
    v = v.replace(/\\"(?=[}\],])/g, '"');

    return v;
}

function flattenObjectKeys(obj: any, prefix = ""): string[] {
    const keys: string[] = [];

    for (const key in obj) {
        const value = obj[key];
        const fullKey = prefix ? `${prefix}.${key}` : key;

        if (value && typeof value === "object" && !Array.isArray(value)) {
            keys.push(...flattenObjectKeys(value, fullKey));
        } else {
            keys.push(fullKey);
        }
    }

    return keys;
}

function assignNestedKey(obj: any, path: string, value: any): void {
    const parts = path.split(".");
    let current = obj;

    for (let i = 0; i < parts.length; i++) {
        const part = parts[i];

        if (i === parts.length - 1) {
            current[part] = value;
        } else {
            current[part] ??= {};
            current = current[part];
        }
    }
}
